import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SharedTable from 'shared/ui/Table';
import { useSelector, useDispatch } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import { withBreakpoints } from 'react-breakpoints';
import {
  selectVulnsSelected,
  selectVulnsList,
  selectIsFetching,
  selectIsFetchingExpandedData,
  selectShowDetail,
  selectShowQuickPlanner
} from 'store/Manage/selectors';
import { setOrderByVulns, expandGroupByVulns } from 'store/Manage/filterActions';
import { selectGroupByField, selectFilters } from 'store/Filters/selectors';
import { selectCustomAttributes } from 'store/CustomAttributes/selectors';
import { selectManageFields } from 'store/Preferences/manage/contextSelectors';
import {
  showContextMenu,
  selectRow,
  unSelectAll,
  hideVulnerabilityDetail
} from 'store/Manage/actions';
import { setFieldWidth } from 'store/Preferences/vulnsGeneral/actions';
import { PropTypes } from 'prop-types';
import { selectPathname } from 'store/Router/selectors';
import {
  showHostDetailById,
  hideHostDetail,
  loadServiceDetail,
  hideServiceDetail
} from 'store/Contextualization/AssetDetail/actions';
import {
  selectExploreGroupPayload,
  selectExploreGroupActiveTab,
  selectExploreGroupHosts,
  selectExploreGroupServices,
  selectIsExploreGroupHostsFetching,
  selectIsExploreGroupServicesFetching,
  selectIsExploreGroupOpen,
  selectExploreGroupSelectedHostIds,
  selectExploreGroupSelectedServiceIds
} from 'store/GroupVulns/selectors';
import {
  setExploreGroupSelectedHostIds,
  setExploreGroupSelectedServiceIds
} from 'store/GroupVulns/actions';
import manageVulnsSchema from './schema';
import exploreServicesSchema from './exploreServicesSchema';
import exploreHostsSchema from './exploreHostsSchema';
import { handleResizedChange, getThProps, getTrProps, findSelectedVuln } from './Functions';
import SubTable from './components/SubTable';
import { selectRiskScoreFeatureFlag } from 'store/Faraday/selectors';
import { selectFields as selectHostFields } from 'store/Preferences/host/contextSelectors';
import { selectFields as selectServiceFields } from 'store/Preferences/service/selectors';
import useTheme from 'Hooks/UseTheme';
import { get } from 'lodash';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_TAGS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';

const Table = ({ currentBreakpoint }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState([]);
  const selectedExploreHostIds = useSelector(selectExploreGroupSelectedHostIds);
  const selectedExploreServiceIds = useSelector(selectExploreGroupSelectedServiceIds);
  const groupByField = useSelector((state) => selectGroupByField('vulns', state));
  const customFields = useSelector(selectCustomAttributes);
  const showVulnDetail = useSelector(selectShowDetail);
  const vulnsSelected = useSelector(selectVulnsSelected);
  const isFetching = useSelector(selectIsFetching);
  const isFetchingExpandedData = useSelector(selectIsFetchingExpandedData);
  const dataRaw = useSelector(selectVulnsList);
  const pathname = useSelector(selectPathname);
  const explorePayload = useSelector(selectExploreGroupPayload);
  const exploreActiveTab = useSelector(selectExploreGroupActiveTab);
  const exploreHostsRaw = useSelector(selectExploreGroupHosts);
  const exploreServicesRaw = useSelector(selectExploreGroupServices);
  const isExploreHostsFetching = useSelector(selectIsExploreGroupHostsFetching);
  const isExploreServicesFetching = useSelector(selectIsExploreGroupServicesFetching);
  const isExploreGroupOpen = useSelector(selectIsExploreGroupOpen);
  const exploreRouteMatch = matchPath(pathname, {
    path: '/vulns/:ws/groups/:groupId',
    exact: true,
    strict: false
  });
  const routeGroupId = get(exploreRouteMatch, 'params.groupId', null);
  const resolvedRouteGroupId = (routeGroupId && routeGroupId !== 'new') ? routeGroupId : null;
  const payloadGroupId = get(explorePayload, 'groupId', get(explorePayload, 'groupHeaderId', null));
  const exploreGroupId = resolvedRouteGroupId ?? payloadGroupId;
  const isExploreContext = Boolean(exploreRouteMatch) || isExploreGroupOpen;
  const isExploreAssetsTab = isExploreContext && exploreActiveTab === 1;
  const isExploreServicesTab = isExploreContext && exploreActiveTab === 2;

  const data = useMemo(() => {
    const counts = {};

    dataRaw.forEach((v) => {
      const isAutomatic = get(v, 'is_automatic', null);
      if (isAutomatic === null || typeof isAutomatic === 'undefined') return;

      const groupId = get(v, 'group_id', null);
      const isMain = get(v, 'is_main', null);
      const groupVulnId = get(v, 'group_vuln_id', null);

      const groupKey = groupId ?? groupVulnId ?? (isMain === true ? v._id : null);
      if (!groupKey) return;

      counts[groupKey] = (counts[groupKey] ?? 0) + 1;
    });

    const enrichedData = dataRaw.map((v) => {
      const isAutomatic = get(v, 'is_automatic', null);
      const isGrouped = !(isAutomatic === null || typeof isAutomatic === 'undefined');

      if (!isGrouped) {
        return { ...v, _groupKey: null, _groupCount: null, _isGrouped: false };
      }

      const groupId = get(v, 'group_id', null);
      const isMain = get(v, 'is_main', null);
      const groupVulnId = get(v, 'group_vuln_id', null);
      const groupKey = groupId ?? groupVulnId ?? (isMain === true ? v._id : null);

      return {
        ...v,
        _isGrouped: !!groupKey,
        _groupKey: groupKey,
        _groupCount: groupKey ? (counts[groupKey] ?? 1) : null
      };
    });

    if (isExploreContext && exploreGroupId !== null && typeof exploreGroupId !== 'undefined') {
      return enrichedData.filter((v) => {
        const itemId = get(v, '_id', get(v, 'id', null));
        const groupId = get(v, 'group_id', null);
        const isMain = get(v, 'is_main', null);
        const groupVulnId = get(v, 'group_vuln_id', null);
        const groupKey = groupId ?? groupVulnId ?? (isMain === true ? itemId : null);

        if (groupKey === null || typeof groupKey === 'undefined') return false;
        return String(groupKey) === String(exploreGroupId);
      });
    }

    if (groupByField) return enrichedData;

    return enrichedData.filter((v) => get(v, 'is_main', null) !== false);
  }, [dataRaw, groupByField, isExploreContext, exploreGroupId]);

  const fields = useSelector(selectManageFields);
  const hostFields = useSelector(selectHostFields);
  const serviceFields = useSelector(selectServiceFields);
  const filters = useSelector((state) => selectFilters('vulns', state));
  const isAllowedByLicense = useSelector(selectRiskScoreFeatureFlag);
  const isTagsAllowedByLicense = useSelector((state) => selectAllowedByRole(state, FEATURE_TAGS, PERMISSION_TYPES.READ));

  const noDataText = isFetching ? '' : <FormattedMessage id="workspaces.table.noData" />;
  const showQuickPlanner = useSelector(selectShowQuickPlanner);
  const { themeStyles } = useTheme();
  const context = {
    isPivot: true,
    customFields,
    showVulnDetail,
    currentBreakpoint,
    groupBy: groupByField,
    fields,
    isFetching,
    isFetchingExpandedData,
    hasGroupedData: null,
    isAllowedByLicense,
    isTagsAllowedByLicense,
    vulnsAssetTable: false,
    showQuickPlanner,
    theme: themeStyles
  };
  const getSubTableContext = (hasGroupedData) => ({
    ...context,
    isPivot: false,
    hasGroupedData,
    isAllowedByLicense: false
  });
  const exploreHostsData = useMemo(() => {
    if (!Array.isArray(exploreHostsRaw)) return [];
    return exploreHostsRaw
      .map((row) => row?.value ?? row)
      .filter(Boolean);
  }, [exploreHostsRaw]);
  const exploreServicesData = useMemo(() => {
    if (!Array.isArray(exploreServicesRaw)) return [];
    return exploreServicesRaw
      .map((row) => row?.value ?? row)
      .filter(Boolean);
  }, [exploreServicesRaw]);
  const serviceIds = useMemo(
    () => exploreServicesData
      .map((row) => get(row, '_id', get(row, 'id', null)))
      .filter((id) => id !== null && typeof id !== 'undefined'),
    [exploreServicesData]
  );
  const allExploreServicesSelected = serviceIds.length > 0 &&
    serviceIds.every((id) => selectedExploreServiceIds.includes(String(id)));
  const hostIds = useMemo(
    () => exploreHostsData
      .map((row) => get(row, '_id', get(row, 'id', null)))
      .filter((id) => id !== null && typeof id !== 'undefined'),
    [exploreHostsData]
  );
  const allExploreHostsSelected = hostIds.length > 0 &&
    hostIds.every((id) => selectedExploreHostIds.includes(String(id)));
  const toggleExploreHostRow = useCallback((row) => {
    const rowId = get(row, '_id', get(row, 'id', null));
    if (rowId === null || typeof rowId === 'undefined') return;
    const normalizedId = String(rowId);
    const next = selectedExploreHostIds.includes(normalizedId)
      ? selectedExploreHostIds.filter((id) => id !== normalizedId)
      : [...selectedExploreHostIds, normalizedId];
    dispatch(setExploreGroupSelectedHostIds(next));
  }, [dispatch, selectedExploreHostIds]);
  const toggleAllExploreHosts = useCallback(() => {
    const next = allExploreHostsSelected ? [] : hostIds.map(String);
    dispatch(setExploreGroupSelectedHostIds(next));
  }, [dispatch, allExploreHostsSelected, hostIds]);
  const hostContext = useMemo(() => ({
    fields: hostFields,
    selectedRowIds: selectedExploreHostIds,
    allSelected: allExploreHostsSelected,
    onToggleRow: toggleExploreHostRow,
    onToggleAll: toggleAllExploreHosts,
    onOpenHostDetail: (host) => {
      const hostId = get(host, 'id', get(host, '_id', 0));
      dispatch(hideVulnerabilityDetail());
      dispatch(hideServiceDetail());
      dispatch(showHostDetailById(hostId));
    }
  }), [dispatch, hostFields, selectedExploreHostIds, allExploreHostsSelected, toggleExploreHostRow, toggleAllExploreHosts]);
  const toggleExploreServiceRow = useCallback((row) => {
    const rowId = get(row, '_id', get(row, 'id', null));
    if (rowId === null || typeof rowId === 'undefined') return;
    const normalizedId = String(rowId);
    const next = selectedExploreServiceIds.includes(normalizedId)
      ? selectedExploreServiceIds.filter((id) => id !== normalizedId)
      : [...selectedExploreServiceIds, normalizedId];
    dispatch(setExploreGroupSelectedServiceIds(next));
  }, [dispatch, selectedExploreServiceIds]);
  const toggleAllExploreServices = useCallback(() => {
    const next = allExploreServicesSelected ? [] : serviceIds.map(String);
    dispatch(setExploreGroupSelectedServiceIds(next));
  }, [dispatch, allExploreServicesSelected, serviceIds]);
  const serviceContext = useMemo(() => ({
    fields: serviceFields,
    selectedRowIds: selectedExploreServiceIds,
    allSelected: allExploreServicesSelected,
    onToggleRow: toggleExploreServiceRow,
    onToggleAll: toggleAllExploreServices,
    onOpenServiceDetail: (service) => {
      const serviceId = get(service, '_id', get(service, 'id', 0));
      dispatch(hideVulnerabilityDetail());
      dispatch(hideHostDetail());
      dispatch(loadServiceDetail(serviceId));
    },
    isTagsAllowedByLicense
  }), [dispatch, serviceFields, selectedExploreServiceIds, allExploreServicesSelected, toggleExploreServiceRow, toggleAllExploreServices, isTagsAllowedByLicense]);

  const selectRowAction = (row) => dispatch(selectRow({}, row));
  const unSelectAllAction = (row) => dispatch(unSelectAll({}, row));
  const setFieldWidthAction = (field, value) => dispatch(setFieldWidth(field, value, 'manage_table'));
  const showContextMenuAction = (value, xPos, yPos) => dispatch(showContextMenu(value, xPos, yPos));

  const onExpandedChange = (expanded, index) => {
    if (expanded[index]) {
      const expandData = data[index];
      dispatch(expandGroupByVulns(index, expandData));
    }
    setExpanded(expanded);
  };

  const handleResizeColumn = useCallback(
    debounce((newResized) => {
      if (!groupByField) handleResizedChange(newResized, fields, setFieldWidthAction);
    }, 300),
    [groupByField, fields]
  );

  const fetchData = (sorted) => !isFetching && dispatch(setOrderByVulns(sorted));

  useEffect(() => {
    setExpanded([]);
  }, [groupByField, filters]);

  useEffect(() => {
    return () => {
      handleResizeColumn.cancel();
    };
  }, [handleResizeColumn]);

  if (isExploreAssetsTab) {
    return (
      <SharedTable
        key="explore-group-assets-table"
        schema={ exploreHostsSchema }
        context={ hostContext }
        data={ exploreHostsData }
        isLoading={ isExploreHostsFetching }
        manual={ false }
        SubComponent={ null }
        isRowSelected={ (rowInfo) => {
          const rowId = get(rowInfo.original, '_id', get(rowInfo.original, 'id', null));
          return rowId !== null && selectedExploreHostIds.includes(String(rowId));
        } }
        noDataText={ !isExploreHostsFetching && <FormattedMessage id="app.nodata.context" /> }
      />
    );
  }

  if (isExploreServicesTab) {
    return (
      <SharedTable
        key="explore-group-services-table"
        schema={ exploreServicesSchema }
        context={ serviceContext }
        data={ exploreServicesData }
        isLoading={ isExploreServicesFetching }
        manual={ false }
        SubComponent={ null }
        isRowSelected={ (rowInfo) => {
          const rowId = get(rowInfo.original, '_id', get(rowInfo.original, 'id', null));
          return rowId !== null && selectedExploreServiceIds.includes(String(rowId));
        } }
        noDataText={ !isExploreServicesFetching && <FormattedMessage id="app.nodata.context" /> }
      />
    );
  }

  return (
    <SharedTable
      schema={ manageVulnsSchema }
      context={ context }
      data={ data }
      isLoading={ isFetching && !groupByField }
      onFetch={ fetchData }
      getRowProps={ (rowInfo) => getTrProps(rowInfo, vulnsSelected, selectRowAction, unSelectAllAction, showContextMenuAction, isFetching) }
      isRowSelected={ (rowInfo) => !!findSelectedVuln(rowInfo, vulnsSelected) }
      getHeaderProps={ (column) => getThProps(column, fields, currentBreakpoint, showVulnDetail, showContextMenuAction, showContextMenu) }
      onResize={ handleResizeColumn }
      expanded={ expanded }
      onExpandedChange={ onExpandedChange }
      noDataText={ noDataText }
      SubComponent={ (row) => <SubTable row={ row } getContext={ getSubTableContext } /> }
    />
  );
};

Table.propTypes = {
  currentBreakpoint: PropTypes.string.isRequired
};

export default withBreakpoints(Table);
